/**
 * SatisfactionCell — the one manually-entered column.
 *
 * Layout, top to bottom: the score, then the previous value beneath it, which
 * opens the full history. Notes live in the tabbed panel below the table, not
 * in here — a paragraph has no place in a dense numeric grid.
 *
 * The scale is inverted: 1 (very disappointed if Hourglass went away) is good,
 * 4 (very happy) is bad. Colour is binary — 1-2 green, 3-4 red — because there
 * is no meaningful middle in a four-point forced-choice question.
 */

import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import {
	SATISFACTION_LABELS,
	satisfactionClass,
	isSatisfactionScore,
	type SatisfactionPair,
	type SatisfactionScore,
} from "../useSatisfaction";

/** `createdAt` is a full ISO timestamp; only the date is meaningful here. */
function formatDate(iso: string): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
	return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

interface SatisfactionCellProps {
	pair: SatisfactionPair | undefined;
	saving: boolean;
	onRecord: (score: SatisfactionScore) => void;
	onOpenHistory: () => void;
}

export function SatisfactionCell({
	pair,
	saving,
	onRecord,
	onOpenHistory,
}: SatisfactionCellProps) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState("");
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (editing) inputRef.current?.select();
	}, [editing]);

	const current = pair?.current ?? null;
	const previous = pair?.previous ?? null;

	function startEditing() {
		setDraft(current?.score != null ? String(current.score) : "");
		setEditing(true);
	}

	function commit() {
		const parsed = Number(draft);
		// Enter locks it in — but only a valid 1-4 commits. Anything else simply
		// closes the editor rather than writing a score nobody can interpret.
		if (isSatisfactionScore(parsed)) onRecord(parsed);
		setEditing(false);
	}

	// `current` is ranked over scored rows only, so a present `current` always
	// carries a score. Absent means nobody has scored this timekeeper.

	return (
		<div className="flex flex-col items-end gap-0.5">
			{editing ? (
				<input
					ref={inputRef}
					// Not type="number": the spinner and scroll-to-change are hazards on
					// a four-value scale where a stray wheel event writes a new row.
					type="text"
					inputMode="numeric"
					value={draft}
					onChange={(e) => setDraft(e.target.value.replace(/[^1-4]/g, ""))}
					onKeyDown={(e) => {
						if (e.key === "Enter") commit();
						if (e.key === "Escape") setEditing(false);
					}}
					onBlur={() => setEditing(false)}
					maxLength={1}
					aria-label="Satisfaction score, 1 to 4"
					className="w-9 rounded border bg-background px-1 py-0.5 text-right text-sm tabular-nums outline-none ring-1 ring-ring"
				/>
			) : (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={startEditing}
							disabled={saving}
							aria-label={
								current
									? `Satisfaction ${current.score}. Click to change.`
									: "No satisfaction score. Click to add."
							}
							className={cn(
								"min-w-7 rounded px-1.5 py-0.5 text-sm font-semibold tabular-nums",
								current
									? satisfactionClass(current.score)
									: "text-muted-foreground hover:bg-muted",
								saving && "opacity-50",
							)}
						>
							{current ? current.score : "—"}
						</button>
					</TooltipTrigger>
					<TooltipContent>
						{current?.score != null
							? (SATISFACTION_LABELS[current.score as SatisfactionScore] ??
								`Score ${current.score}`)
							: "Click to record a score"}
					</TooltipContent>
				</Tooltip>
			)}

			{previous ? (
				<button
					type="button"
					onClick={onOpenHistory}
					className="text-[10px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
				>
					{previous.score} on {formatDate(previous.createdAt)}
				</button>
			) : (
				<button
					type="button"
					onClick={onOpenHistory}
					disabled={!current}
					className="text-[10px] text-muted-foreground disabled:cursor-default"
				>
					n/a
				</button>
			)}
		</div>
	);
}
